import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { api, fileUrl } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1600&q=80";

export default function About() {
    const [data, setData] = useState(null);

    useEffect(() => { api.get("/about").then((r) => setData(r.data)); }, []);

    return (
        <PublicLayout>
            <section className="relative overflow-hidden bg-brand-blue text-white">
                <div className="absolute inset-0 opacity-20">
                    <img src={HERO} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="container-x relative py-20 md:py-28">
                    <Breadcrumbs items={[{ label: "About Us" }]} />
                    <h1 className="mt-4 font-serif text-5xl leading-tight sm:text-6xl">Our Story</h1>
                    <p className="mt-5 max-w-2xl text-lg text-white/80">A federation built on the belief that great communities are governed together, transparently and with care.</p>
                </div>
            </section>

            <section className="container-x py-20">
                <div className="grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-8">
                        <div className="overline">Who We Are</div>
                        <h2 className="section-title mt-3">About the Association</h2>
                        <p className="mt-6 text-lg leading-relaxed text-slate-600">{data?.about}</p>

                        <div className="mt-10">
                            <div className="overline">History</div>
                            <h3 className="mt-3 font-serif text-3xl text-slate-900">Two Decades of Community Service</h3>
                            <p className="mt-4 leading-relaxed text-slate-600">{data?.history}</p>
                        </div>

                        <div className="mt-12 grid gap-6 sm:grid-cols-2">
                            <div className="surface-card p-7">
                                <div className="overline text-brand-orange">Vision</div>
                                <p className="mt-3 leading-relaxed text-slate-600">{data?.vision}</p>
                            </div>
                            <div className="surface-card p-7">
                                <div className="overline text-brand-orange">Mission</div>
                                <p className="mt-3 leading-relaxed text-slate-600">{data?.mission}</p>
                            </div>
                        </div>

                        <div className="mt-12">
                            <div className="overline">Objectives</div>
                            <h3 className="mt-3 font-serif text-3xl text-slate-900">What We Stand For</h3>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                {(data?.objectives || []).map((o) => (
                                    <div key={o.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`objective-${o.title}`}>
                                        <div className="flex items-start gap-3">
                                            <Icons.CheckCircle2 size={18} className="mt-1 shrink-0 text-brand-orange" />
                                            <div>
                                                <div className="font-serif text-lg text-slate-900">{o.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{o.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <aside className="md:col-span-4">
                        <div className="sticky top-24 space-y-6">
                            <div className="surface-card p-6">
                                <div className="overline">Registration</div>
                                <h4 className="mt-3 font-serif text-2xl text-slate-900">Registered Association</h4>
                                <dl className="mt-5 space-y-3 text-sm">
                                    <div>
                                        <dt className="text-slate-500">Registration Number</dt>
                                        <dd className="font-medium text-slate-900">{data?.registration_number || "—"}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-500">Registration Date</dt>
                                        <dd className="font-medium text-slate-900">{data?.registration_date || "—"}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-500">Registration Authority</dt>
                                        <dd className="font-medium text-slate-900">{data?.registration_authority || "—"}</dd>
                                    </div>
                                </dl>
                                {data?.registration_certificate_id && (
                                    <a
                                        href={fileUrl(data.registration_certificate_id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-outline mt-5 w-full"
                                        data-testid="download-registration-cert"
                                    >
                                        View Certificate <Icons.ExternalLink size={14} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </PublicLayout>
    );
}
